import { traerUbicacionYPernocte } from "./ubicacionPernocte";
import { traerPlanilla, guardarPlanilla, CONFLICTO } from "./espacio";
import {
  renglonesParaUbicar, clavesDeRenglones, traeUbicacionPlanilla, ponerUbicacion,
} from "../shared/ubicacionAlAgregar";
import { hoyAr } from "../shared/fechaAr";

/* Después de «Agregar … a la planilla» en Torrecillas (ARRIBOS BITREN) y en
 * las salidas de Gula (SALIDAS BITREN): la UBICACIÓN de los renglones
 * nuevos. Lautaro, 21/10: «que también ponga la posición actual». Las reglas
 * de qué se escribe, en `shared/ubicacionAlAgregar`.
 *
 * ── NO FRENA EL AGREGADO ────────────────────────────────────────────────────
 *
 * Los renglones ya quedaron guardados cuando se llama esto: si FlotaBot no
 * contesta, tarda más de `ESPERA_MS` o es el bot de antes, la UBICACIÓN
 * queda vacía como hasta el 21/10 y no se reintenta (la completa el job de
 * las 08:00). Nada de acá lanza: devuelve qué pasó, para que la pantalla
 * diga una línea aparte del aviso del agregado.
 *
 * ── CÓMO ESCRIBE ────────────────────────────────────────────────────────────
 *
 * Como el botón: se le pregunta a FlotaBot y DESPUÉS se vuelve a traer la
 * planilla, porque él puede haber escrito en esos segundos. Se guarda con el
 * candado de `actualizado_en`, y si chocó con un guardado suyo se intenta
 * una vez más sobre la de ahora: lo que se escribe es sólo en celdas vacías
 * y de renglones que siguen diciendo el mismo TRA, así que hacerlo de nuevo
 * no pisa nada. Sin foto al Historial aparte: llena celdas vacías de los
 * renglones que se acaban de agregar, y la foto de antes del agregado (en
 * SALIDAS BITREN) ya es la de cómo estaba.
 *
 * `hojaDe(datos)` es el índice de la hoja (`hojaDeArribos` o
 * `hojaDeSalidas`); `hoja`, la que se acaba de guardar, y `nuevas`, sus
 * renglones agregados. Devuelve `{estado, escritas}`, con estado:
 *   "ok"          se escribió (escritas > 0) o no hubo nada para escribir;
 *   "nada"        ningún renglón nuevo con un tractor para buscar;
 *   "bot-viejo"   FlotaBot no trae `ubicacion_planilla`: no se escribe;
 *   "sin-respuesta" FlotaBot no contestó, contestó con error o tardó;
 *   "no-guardo"   no se pudo traer o guardar la planilla. */

export const ESPERA_MS = 20000;

/* La respuesta de FlotaBot, o `{ok: false}` si pasan `ms` antes. */
async function conEspera(promesa, ms) {
  let reloj;
  const tarde = new Promise((resolve) => {
    reloj = setTimeout(() => resolve({ ok: false, mensaje: "tardó" }), ms);
  });
  try {
    return await Promise.race([promesa, tarde]);
  } finally {
    clearTimeout(reloj);
  }
}

export async function ponerUbicacionAlAgregar({
  planillaId, hojaDe, hoja, nuevas, espera = ESPERA_MS,
}) {
  const renglones = renglonesParaUbicar(hoja, nuevas);
  if (!planillaId || !renglones.length) return { estado: "nada", escritas: 0 };
  let r;
  try {
    r = await conEspera(traerUbicacionYPernocte(clavesDeRenglones(renglones), hoyAr()), espera);
  } catch {
    r = null;
  }
  if (!r?.ok) return { estado: "sin-respuesta", escritas: 0 };
  if (!traeUbicacionPlanilla(r.unidades)) return { estado: "bot-viejo", escritas: 0 };
  for (let intento = 0; intento < 2; intento += 1) {
    try {
      const p = await traerPlanilla(planillaId);
      const i = hojaDe(p.datos);
      if (i < 0) return { estado: "no-guardo", escritas: 0 };
      const x = ponerUbicacion(p.datos.hojas[i], renglones, r.unidades);
      if (!x.escritas.length) return { estado: "ok", escritas: 0 };
      const hojas = p.datos.hojas.map((h, j) => (j === i ? x.hoja : h));
      await guardarPlanilla(p.id, p.nombre, { ...p.datos, hojas }, p.actualizado_en);
      return { estado: "ok", escritas: x.escritas.length };
    } catch (e) {
      if (e?.codigo !== CONFLICTO || intento === 1) return { estado: "no-guardo", escritas: 0 };
    }
  }
  return { estado: "no-guardo", escritas: 0 };
}

/* Lo que dice la pantalla, aparte del aviso del agregado, o "" si no hay
   nada que decir: el bot de antes y «nada para buscar» no se dicen (la
   celda vacía es la de siempre). */
export function textoUbicacionAlAgregar({ estado, escritas } = {}) {
  if (estado === "ok" && escritas) {
    return `Completé la UBICACIÓN de ${escritas} ${escritas === 1 ? "renglón" : "renglones"} con la posición de ahora.`;
  }
  if (estado === "sin-respuesta") return "No pude traer la posición de ahora: la UBICACIÓN quedó vacía.";
  if (estado === "no-guardo") return "No pude escribir la posición de ahora: la UBICACIÓN quedó vacía.";
  return "";
}
